import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/auction/StatusBadge";
import { CountdownPill } from "@/components/auction/CountdownPill";
import { ShipmentTracker, ReturnStatusBadge } from "@/components/orders/ShipmentTracker";
import { Skeleton } from "@/components/ui/skeleton";
import { Lock, Trophy, CreditCard, Package, Scale, RotateCcw, Truck, Upload, X, Loader2, CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StarInput, StarRating } from "@/components/reviews/StarRating";

function Empty({ icon: Icon, title, text, cta }) {
    return (
        <div className="rounded-xl border border-dashed border-border p-12 text-center">
            <Icon className="mx-auto h-8 w-8 text-muted-foreground/50" />
            <p className="mt-3 font-display font-semibold">{title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{text}</p>
            {cta}
        </div>
    );
}

export default function BuyerDashboard() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [bids, setBids] = useState(null);
    const [won, setWon] = useState(null);

    const tab = params.get("tab") || "bids";
    const setTab = (t) => { const next = Object.fromEntries(params.entries()); setParams({ ...next, tab: t }); };

    useEffect(() => {
        if (!loading && !user) navigate("/login");
    }, [loading, user, navigate]);

    const load = useCallback(() => {
        api.get("/me/bids").then(({ data }) => setBids(data)).catch((e) => { console.error("Failed to load bids:", e); setBids([]); });
        api.get("/me/won").then(({ data }) => setWon(data)).catch((e) => { console.error("Failed to load won items:", e); setWon([]); });
    }, []);
    useEffect(() => { if (user) load(); }, [user, load]);

    const payNow = async (a) => {
        try {
            const { data } = await api.post("/payments/checkout", { auction_id: a.id, origin_url: window.location.origin });
            window.location.assign(data.url);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); }
    };

    if (!user) return null;

    return (
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
            <h1 className="font-display text-3xl font-semibold tracking-tight">Buyer dashboard</h1>
            <p className="text-sm text-muted-foreground">Your sealed bids, wins, and orders.</p>

            <Tabs value={tab} onValueChange={setTab} className="mt-6">
                <TabsList>
                    <TabsTrigger value="bids" data-testid="buyer-tab-bids">My bids</TabsTrigger>
                    <TabsTrigger value="won" data-testid="buyer-tab-won">Won</TabsTrigger>
                    <TabsTrigger value="orders" data-testid="buyer-tab-orders">My Wins / Orders</TabsTrigger>
                </TabsList>

                <TabsContent value="bids" className="mt-4">
                    {bids === null ? <Skeleton className="h-40 w-full rounded-xl" /> : bids.length === 0 ? (
                        <Empty icon={Lock} title="No bids yet" text="Place your first sealed bid on an open auction." cta={<Button asChild className="mt-4 rounded-[10px]"><Link to="/auctions">Browse auctions</Link></Button>} />
                    ) : (
                        <div className="grid gap-3">
                            {bids.map((b) => (
                                <Card key={b.bid_id} data-testid="buyer-bid-row" className="flex flex-wrap items-center justify-between gap-4 p-4">
                                    <div className="flex items-center gap-3">
                                        <img src={b.auction.images?.[0]} alt="" className="h-14 w-14 rounded-lg object-cover" />
                                        <div>
                                            <Link to={`/auctions/${b.auction.id}`} className="font-display font-semibold hover:underline">{b.auction.title}</Link>
                                            <div className="mt-1 flex items-center gap-2"><StatusBadge status={b.auction.status} /><CountdownPill endTime={b.auction.end_time} status={b.auction.status} /></div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs text-muted-foreground">Your sealed bid</p>
                                        <p className="inline-flex items-center gap-1 font-display text-lg font-semibold tabular-nums"><Lock className="h-3.5 w-3.5" />{money(b.amount)}</p>
                                        {b.won && <p className="text-xs font-medium text-brass-foreground">You won — highest bid</p>}
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="won" className="mt-4">
                    {won === null ? <Skeleton className="h-40 w-full rounded-xl" /> : won.length === 0 ? (
                        <Empty icon={Trophy} title="No wins yet" text="When you win an auction, it lands here — ready for payment." />
                    ) : (
                        <div className="grid gap-3">
                            {won.map((a) => (
                                <Card key={a.id} data-testid="buyer-won-row" className="flex flex-wrap items-center justify-between gap-4 p-4">
                                    <div className="flex items-center gap-3">
                                        <img src={a.images?.[0]} alt="" className="h-14 w-14 rounded-lg object-cover" />
                                        <div>
                                            <Link to={`/auctions/${a.id}`} className="font-display font-semibold hover:underline">{a.title}</Link>
                                            <p className="mt-1 text-sm">Winning bid: <span className="font-semibold tabular-nums">{money(a.winning_amount)}</span></p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {a.paid ? <StatusBadge status="PAID" /> : (
                                            <Button data-testid="buyer-pay-button" className="rounded-[10px]" onClick={() => payNow(a)}><CreditCard className="mr-2 h-4 w-4" />Pay now</Button>
                                        )}
                                        <OpenDisputeButton auction={a} onDone={load} />
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="orders" className="mt-4">
                    <OrdersTab />
                </TabsContent>
            </Tabs>
        </div>
    );
}

// ----------------- Orders (tracking + returns) -----------------
function OrdersTab() {
    const [orders, setOrders] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const reload = useCallback(() => setReloadKey((k) => k + 1), []);

    useEffect(() => {
        let active = true;
        api.get("/buyer/orders")
            .then(({ data }) => { if (active) setOrders(data); })
            .catch(() => { if (active) { setOrders([]); toast.error("Failed to load orders"); } });
        return () => { active = false; };
    }, [reloadKey]);

    if (orders === null) return <Skeleton className="h-48 w-full rounded-xl" />;
    if (orders.length === 0) {
        return <Empty icon={Package} title="No orders yet" text="Paid orders appear here with live tracking and returns." />;
    }
    return (
        <div className="grid gap-4">
            {orders.map((o) => <BuyerOrderCard key={o.auction_id} order={o} onChange={reload} />)}
        </div>
    );
}

function BuyerOrderCard({ order, onChange }) {
    const [busy, setBusy] = useState("");
    const r = order.return;
    const canRequestReturn = !r || ["DENIED", "REFUNDED"].includes(r.status);
    const delivered = order.shipment?.status === "DELIVERED";

    const shipReturn = async () => {
        setBusy("ship");
        try {
            await api.post(`/buyer/returns/${r.id}/ship`);
            toast.success("Marked as shipped back");
            onChange();
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setBusy(""); }
    };

    return (
        <Card data-testid="buyer-order-row" className="space-y-4 p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <img src={order.image} alt="" className="h-14 w-14 rounded-lg object-cover" />
                    <div>
                        <Link to={`/auctions/${order.auction_id}`} className="font-display font-semibold hover:underline">{order.title}</Link>
                        <p className="text-xs text-muted-foreground">
                            Seller: {order.seller_id ? <Link to={`/seller/${order.seller_id}`} className="font-medium text-electric hover:underline">{order.seller_name}</Link> : order.seller_name}
                            {" · "}Paid <span className="font-medium text-brass">{money(order.winning_amount)}</span>
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {order.review ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-success/30 bg-success/10 px-2.5 py-1 text-xs font-medium text-success" data-testid="order-reviewed-badge">
                            <CheckCircle2 className="h-3.5 w-3.5" />Reviewed <StarRating value={order.review.overall} size="sm" />
                        </span>
                    ) : delivered ? (
                        <ReviewDialog order={order} onDone={onChange} />
                    ) : null}
                    {canRequestReturn && <ReturnRequestDialog order={order} onDone={onChange} />}
                </div>
            </div>

            {order.shipment ? (
                <ShipmentTracker shipment={order.shipment} />
            ) : (
                <div className="flex items-center gap-2 rounded-xl border border-border bg-secondary/20 p-4 text-sm text-muted-foreground" data-testid="awaiting-shipment">
                    <Package className="h-4 w-4" /> Awaiting shipment from the seller.
                </div>
            )}

            {r && (
                <div className="rounded-xl border border-warning/30 bg-warning/[0.06] p-4" data-testid="buyer-return-panel">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2"><RotateCcw className="h-4 w-4 text-warning" /><span className="text-sm font-semibold">Return</span></div>
                        <ReturnStatusBadge status={r.status} />
                    </div>
                    <p className="mt-2 text-sm"><span className="font-medium">{r.reason}</span>{r.details ? ` — ${r.details}` : ""}</p>
                    {r.seller_note && <p className="mt-1 text-xs text-muted-foreground">Seller: {r.seller_note}</p>}
                    {r.return_tracking_number && <p className="mt-1 text-xs text-muted-foreground">Return label: {r.return_carrier} · {r.return_tracking_number}</p>}
                    {r.status === "REFUNDED" && r.refund_amount != null && <p className="mt-1 text-sm font-medium text-success">Refunded {money(r.refund_amount)}</p>}
                    {r.status === "APPROVED" && (
                        <Button size="sm" className="mt-3 rounded-[10px]" disabled={busy === "ship"} onClick={shipReturn} data-testid="buyer-ship-return-button">
                            {busy === "ship" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Truck className="mr-1.5 h-3.5 w-3.5" />}I&apos;ve shipped it back
                        </Button>
                    )}
                </div>
            )}
        </Card>
    );
}

function ReturnRequestDialog({ order, onDone }) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [details, setDetails] = useState("");
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const onPick = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        for (const f of files) {
            const fd = new FormData();
            fd.append("file", f);
            try { const { data } = await api.post("/uploads/image", fd); setPhotos((p) => [...p, data.url]); }
            catch { toast.error(`Could not upload ${f.name}`); }
        }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
    };

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/buyer/orders/${order.auction_id}/return`, { reason, details, photos });
            toast.success("Return request submitted");
            setOpen(false); setReason(""); setDetails(""); setPhotos([]);
            if (onDone) onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit return"); } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="secondary" className="rounded-[10px]" data-testid="request-return-button"><RotateCcw className="mr-2 h-4 w-4" />Request return</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Request a return</DialogTitle>
                    <DialogDescription>Tell the seller what&apos;s wrong. They&apos;ll approve or deny your request.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Input placeholder="Reason (e.g. item not as described)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="return-reason-input" />
                    <Textarea placeholder="Add details to help the seller…" value={details} onChange={(e) => setDetails(e.target.value)} data-testid="return-details-input" />
                    <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPick} className="hidden" />
                    <Button type="button" variant="secondary" className="w-full rounded-[10px]" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="return-upload-button">
                        {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Add photos
                    </Button>
                    {photos.length > 0 && (
                        <div className="grid grid-cols-4 gap-2">
                            {photos.map((p) => (
                                <div key={p} className="group relative aspect-square overflow-hidden rounded-lg border border-border">
                                    <img src={p} alt="" className="h-full w-full object-cover" />
                                    <button type="button" onClick={() => setPhotos((prev) => prev.filter((x) => x !== p))} className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-background/80 text-destructive opacity-0 transition-opacity group-hover:opacity-100" aria-label="Remove">
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={!reason || busy} className="rounded-[10px]" data-testid="return-submit-button">
                        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}Submit return
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReviewDialog({ order, onDone }) {
    const [open, setOpen] = useState(false);
    const [scores, setScores] = useState({ item_as_described: 0, shipping_speed: 0, communication: 0 });
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);
    const setScore = (k, v) => setScores((s) => ({ ...s, [k]: v }));
    const complete = scores.item_as_described && scores.shipping_speed && scores.communication;
    const overall = complete ? ((scores.item_as_described + scores.shipping_speed + scores.communication) / 3).toFixed(1) : "—";

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/orders/${order.auction_id}/review`, { ...scores, comment });
            toast.success("Thanks for your review!");
            setOpen(false);
            if (onDone) onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit review"); } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="rounded-[10px]" data-testid="leave-review-button"><Star className="mr-2 h-4 w-4" />Leave a review</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Review your order</DialogTitle>
                    <DialogDescription>Rate your experience with <span className="font-medium text-foreground">{order.seller_name}</span> for &ldquo;{order.title}&rdquo;.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-2 rounded-lg border border-border bg-secondary/30 p-3">
                        <StarInput label="Item as described" value={scores.item_as_described} onChange={(v) => setScore("item_as_described", v)} testId="review-item" />
                        <StarInput label="Shipping speed" value={scores.shipping_speed} onChange={(v) => setScore("shipping_speed", v)} testId="review-shipping" />
                        <StarInput label="Communication" value={scores.communication} onChange={(v) => setScore("communication", v)} testId="review-communication" />
                        <div className="flex items-center justify-between border-t border-border pt-2 text-sm">
                            <span className="font-medium">Overall</span>
                            <span className="font-display font-bold tabular-nums text-brass" data-testid="review-overall">{overall}{complete ? "★" : ""}</span>
                        </div>
                    </div>
                    <Textarea placeholder="Share details about your experience (optional)…" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment-input" />
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={!complete || busy} className="rounded-[10px]" data-testid="review-submit-button">
                        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}Submit review
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function OpenDisputeButton({ auction, onDone }) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [details, setDetails] = useState("");
    const submit = async () => {
        try {
            await api.post("/disputes", { auction_id: auction.id, reason, details });
            toast.success("Dispute opened");
            setOpen(false); if (onDone) onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not open dispute"); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button variant="secondary" className="rounded-[10px]" data-testid="open-dispute-button"><Scale className="mr-2 h-4 w-4" />Dispute</Button></DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle>Open a dispute</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <Input placeholder="Reason (e.g. item not as described)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dispute-reason-input" />
                    <Textarea placeholder="Describe the issue..." value={details} onChange={(e) => setDetails(e.target.value)} />
                </div>
                <DialogFooter><Button onClick={submit} disabled={!reason} className="rounded-[10px]" data-testid="dispute-submit-open">Open dispute</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}